import { useRef, useMemo, useState, useCallback } from "react";
import { withRouter } from "react-router-dom";

import {
  ActionToolbar,
  AmenitiesColorsLegend,
  DeleteButton,
  DialogWithActions,
  GenerateAmenitiesReport,
  GqlCalendar,
  GqlCalendarEventBuilder,
  GqlTable,
  LoadWrapper,
  RendererPicker,
  ReservationPreview,
} from "../../components";

import {
  Button,
  CircularProgress,
  Container,
  Typography,
} from "@material-ui/core";
import TableCell from "@material-ui/core/TableCell";
import EventIcon from "@material-ui/icons/Event";
import GridOnIcon from "@material-ui/icons/GridOn";

import { QUERIES, MUTATIONS } from "../../../graphql";
import ReservationForm from "./reservation_form";

import {
  useDateTimeUtils,
  useLoggedUserInfo,
  useSnackbarMessage,
  useSnackbarMutation,
} from "../../../hooks";
import { Reservation } from "../../../graphql/models";
import {
  ReservationFilters,
  ReservationFilterValue,
} from "./reservation_filters";
import useReservationsStyle from "./reservations.style";
import { useAmenitiesColorMap } from "../../../graphql/hooks";
import CsiRating from "../../components/csi_rating/csi_rating";

enum DisplayMode {
  GRID = "Tabella",
  CALENDAR = "Calendar",
}

function ReservationsPage(props: any) {
  const classes = useReservationsStyle();
  const { raiseSuccessSnackbar } = useSnackbarMessage();
  const { isAdminUser } = useLoggedUserInfo();

  const [invokeDeleteReservation, { loading: deleteReservationLoading }] =
    useSnackbarMutation(MUTATIONS.DELETE_AMENITY_RESERVATION, {
      onCompleted: ({ deleteAmenityReservation }) => {
        if (deleteAmenityReservation?.ok) {
          setDeleteDialogOpened(false);
          setPreviewDialogOpened(false);
          (tableRef as any).current?.refresh();
          (calendarRef as any).current?.refresh();
          raiseSuccessSnackbar("Prenotazione eliminata con successo!");
        }
      },
    });

  const [invokeSetDidntShowUp, { loading: invokeSetDidntShowUpLoading }] =
    useSnackbarMutation(MUTATIONS.SET_AMENITY_RESERVATION_DIDNT_SHOW_UP, {
      onCompleted: () => {
        setDidntShowUpDialogOpened(false);
        setPreviewDialogOpened(false);
        (tableRef as any).current?.refresh();
        (calendarRef as any).current?.refresh();
        raiseSuccessSnackbar("Variazione registrata con successo!");
      },
    });

  const tableRef = useRef();
  const calendarRef = useRef();

  // Display Mode: grid | calendar
  const [displayMode, setDisplayMode] = useState<DisplayMode>(
    DisplayMode.CALENDAR
  );

  const isCalendarDisplayMode = useMemo(
    () => displayMode === DisplayMode.CALENDAR,
    [displayMode]
  );

  const isGridDisplayMode = useMemo(
    () => displayMode === DisplayMode.GRID,
    [displayMode]
  );

  const [selectedReservation, setSelectedReservation] = useState<
    Reservation | undefined
  >(void 0);
  const [formDialogOpened, setFormDialogOpened] = useState(false);
  const [didntShowUpDialogOpened, setDidntShowUpDialogOpened] = useState(false);
  const [deleteDialogOpened, setDeleteDialogOpened] = useState(false);
  const [previewDialogOpened, setPreviewDialogOpened] = useState(false);

  const { displayDate } = useDateTimeUtils();

  const dateNow = new Date();
  const dateFrom = new Date();
  const dateTo = new Date();

  dateFrom.setDate(dateNow.getDate() - 15);
  dateTo.setDate(dateNow.getDate() + 15);

  const [filters, setFilters] = useState<ReservationFilterValue>({
    from: dateFrom,
    to: dateTo,
    excludeDeleted: true,
  });

  const { amenityColorMap, loading: colorsLoading } = useAmenitiesColorMap();

  // Event builder for rendering calendar event
  const calendarEventBuilder: GqlCalendarEventBuilder = (
    record: Reservation
  ) => {
    const cssClass = record.deletionDate
      ? classes.deletedCalendarEvent
      : classes.defaultCalendarEvent;

    const amenityColor = amenityColorMap[record.service.id];

    return {
      id: record.id,
      date: record.date,
      start: record.utcStart,
      end: record.utcEnd,
      startEditable: false,
      editable: false,
      className: cssClass,
      durationEditable: false,
      backgroundColor: amenityColor?.color,
      title: `${record.apartment.code} - ${record.service.name} ${
        record.esclusive ? "- ESCLUSIVA -" : `(n. ${record.units} persone)`
      }`,
    };
  };

  const onCalendarEventClick = (evt, clickedElement: Reservation) => {
    if (clickedElement) {
      openPreviewDialog(clickedElement);
    }
  };

  const openPreviewDialog = (reservation: Reservation) => {
    setSelectedReservation(reservation);
    setPreviewDialogOpened(true);
  };

  const closePreviewDialog = useCallback(() => {
    setPreviewDialogOpened(false);
    setSelectedReservation(void 0);
  }, [setPreviewDialogOpened, setSelectedReservation]);

  const addNewReservation = () => {
    setFormDialogOpened(true);
  };

  const onValueUpdatedSuccessfully = () => {
    //setFormReservation(void 0);
    setFormDialogOpened(false);
    (tableRef as any).current?.refresh();
    (calendarRef as any).current?.refresh();
  };

  const triggerDeleteReservation = useCallback(
    (reservation: Reservation) => {
      setSelectedReservation({ ...reservation });
      setDeleteDialogOpened(true);
    },
    [setSelectedReservation, setDeleteDialogOpened]
  );

  const triggerSetDidntShowUp = useCallback(
    (reservation: Reservation) => {
      setSelectedReservation({ ...reservation });
      setDidntShowUpDialogOpened(true);
    },
    [setSelectedReservation, setDidntShowUpDialogOpened]
  );

  const undoDidntShowUp = () => {
    setDidntShowUpDialogOpened(false);
    setPreviewDialogOpened(false);
    setSelectedReservation(void 0);
  };

  const confirmDidntShowUp = () => {
    invokeSetDidntShowUp({
      variables: {
        id: selectedReservation?.id,
        didntShowUp: !selectedReservation?.didntShowUp,
      },
    });
  };

  const undoDelete = () => {
    setDeleteDialogOpened(false);
    setPreviewDialogOpened(false);
    setSelectedReservation(void 0);
  };

  const confirmDelete = () => {
    invokeDeleteReservation({
      variables: {
        id: selectedReservation?.id,
      },
    });
  };

  const amenityReservationsSearchQueryParams = useMemo(() => {
    return {
      from: filters.from,
      to: filters.to,
      serviceId: filters.amenity?.id,
      apartmentIds:
        filters.apartments && filters.apartments.length > 0
          ? filters.apartments.map((v) => v.id)
          : void 0,
      excludeDeleted: filters.excludeDeleted,
    };
  }, [filters]);

  // Dialog Actions
  const previewDialogActions = useMemo(() => {
    const actions: any[] = [];

    if (!selectedReservation?.deletionDate) {
      actions.push(
        <Button
          key="triggerDeleteReservation"
          onClick={() =>
            triggerDeleteReservation(selectedReservation as Reservation)
          }
          color="secondary"
        >
          ANNULLA PRENOTAZIONE
        </Button>
      );
    }
    actions.push(
      <Button
        key="triggerSetDidntShowUp"
        onClick={() =>
          triggerSetDidntShowUp(selectedReservation as Reservation)
        }
        color="default"
      >
        {selectedReservation?.didntShowUp === true
          ? "IL CLIENTE SI È PRESENTATO"
          : "IL CLIENTE NON SI È PRESENTATO"}
      </Button>
    );

    actions.push(
      <Button
        key="closePreviewDialog"
        onClick={() => closePreviewDialog()}
        color="primary"
        autoFocus
      >
        Chiudi
      </Button>
    );

    return actions;
  }, [
    selectedReservation,
    closePreviewDialog,
    triggerDeleteReservation,
    triggerSetDidntShowUp,
  ]);

  const gqlTableColumns = useMemo(() => {
    const columns = [
      "",
      "Data",
      "Cod. Prenotazione",
      "Amenity",
      "Appartamento",
      "Creato Da",
      "N. Persone",
      "Quando",
    ];

    if (isAdminUser) {
      columns.push("Rating");
    }

    return columns;
  }, [isAdminUser]);

  return (
    <LoadWrapper loading={colorsLoading}>
      <Container className={classes.container} maxWidth={false} component="div">
        <>
          <ActionToolbar
            title={"Prenotazioni"}
            actions={[
              <RendererPicker
                key="setDisplayMode"
                value={displayMode}
                onChange={(v) => setDisplayMode(v as DisplayMode)}
                options={[
                  {
                    value: DisplayMode.CALENDAR,
                    renderer: <EventIcon />,
                  },
                  {
                    value: DisplayMode.GRID,
                    renderer: <GridOnIcon />,
                  },
                ]}
                orientation="horizontal"
              />,
              <Button
                key="addNewReservation"
                variant="contained"
                color="primary"
                onClick={() => addNewReservation()}
              >
                prenota
              </Button>,
              <ReservationFilters
                key="setFilters"
                value={filters}
                setValue={setFilters}
              />,
              <GenerateAmenitiesReport
                key="GenerateAmenitiesReport"
                amenityReservationsFilters={
                  amenityReservationsSearchQueryParams
                }
              />,
            ]}
          />
          {isCalendarDisplayMode && (
            <div className={classes.reservationsCalendar}>
              <div style={{ width: "100%", padding: "0.5rem" }}>
                <AmenitiesColorsLegend />
              </div>
              <GqlCalendar
                ref={calendarRef}
                query={QUERIES.AMENITY_RESERVATIONS}
                queryResultDataField="amenityReservations"
                queryVariables={amenityReservationsSearchQueryParams}
                eventBuilder={calendarEventBuilder}
                eventClick={onCalendarEventClick}
                visibleFrom={filters.from}
                visibleTo={filters.to}
                autoRefreshTimeoutInSeconds={30}
              />
            </div>
          )}
          {isGridDisplayMode && (
            <GqlTable
              ref={tableRef}
              autoRefreshTimeoutInSeconds={30}
              columns={gqlTableColumns.map((v) => (
                <TableCell>{v}</TableCell>
              ))}
              query={QUERIES.AMENITY_RESERVATIONS}
              queryResultDataField="amenityReservations"
              queryVariables={amenityReservationsSearchQueryParams}
              rows={(row: Reservation) => (
                <>
                  <TableCell>
                    {!row.deletionDate && (
                      <DeleteButton
                        onClick={() => triggerDeleteReservation(row)}
                      />
                    )}
                    {!!row.deletionDate && <strong>ANNULLATA</strong>}
                  </TableCell>
                  <TableCell>{displayDate(row.date)}</TableCell>
                  <TableCell>{row.code}</TableCell>
                  <TableCell>{row.service.name}</TableCell>
                  <TableCell>{row.apartment.code}</TableCell>
                  <TableCell>{row.createdBy.name}</TableCell>
                  <TableCell>
                    {row.esclusive ? "-- ESCLUSIVA --" : row.units}
                  </TableCell>
                  <TableCell>{row.bookingTexts?.rangeText}</TableCell>
                  {isAdminUser && (
                    <TableCell>
                      {row.customerSatisfaction && (
                        <CsiRating
                          customerSatisfaction={row.customerSatisfaction}
                        />
                      )}
                    </TableCell>
                  )}
                </>
              )}
            />
          )}

          {/* -- Dialog creazione nuova prenotazione -- */}
          <DialogWithActions
            fullScreen
            open={formDialogOpened}
            setOpen={setFormDialogOpened}
            title={"Nuova Prenotazione"}
          >
            <ReservationForm
              onReservationCreatedSuccessfully={onValueUpdatedSuccessfully}
            />
          </DialogWithActions>
          {/* -- Popup eliminazione prenotazione esistente -- */}
          <DialogWithActions
            fullScreen={false}
            open={deleteDialogOpened}
            setOpen={setDeleteDialogOpened}
            title={"Conferma cancellazione"}
            actions={[
              <Button
                key="undoDelete"
                disabled={deleteReservationLoading}
                onClick={undoDelete}
                color="primary"
              >
                Annulla
              </Button>,
              <Button
                key="confirmDelete"
                disabled={deleteReservationLoading}
                onClick={confirmDelete}
                color="secondary"
                autoFocus
              >
                Confermo
              </Button>,
            ]}
          >
            {!deleteReservationLoading && (
              <Typography>
                Confermi di voler annullare la prenotazione?
              </Typography>
            )}
            {deleteReservationLoading && (
              <span>
                <CircularProgress color="inherit" />
              </span>
            )}
          </DialogWithActions>
          {/* -- Popup di trigger no show -- */}
          <DialogWithActions
            fullScreen={false}
            open={didntShowUpDialogOpened}
            setOpen={setDidntShowUpDialogOpened}
            title={"Variazione presenza prenotazione"}
            actions={[
              <Button
                key="undoDidntShowUp"
                disabled={invokeSetDidntShowUpLoading}
                onClick={undoDidntShowUp}
                color="primary"
              >
                Annulla
              </Button>,
              <Button
                key="confirmDidntShowUp"
                disabled={invokeSetDidntShowUpLoading}
                onClick={confirmDidntShowUp}
                color="secondary"
                autoFocus
              >
                Confermo
              </Button>,
            ]}
          >
            {!invokeSetDidntShowUpLoading && (
              <Typography>
                {selectedReservation?.didntShowUp === true &&
                  "Confermi che il condomino si è presentato all'appuntamento?"}
                {!selectedReservation?.didntShowUp &&
                  "Confermi che il condomino NON si è presentato all'appuntamento?"}
              </Typography>
            )}
            {invokeSetDidntShowUpLoading && (
              <span>
                <CircularProgress color="inherit" />
              </span>
            )}
          </DialogWithActions>
          {/* -- Popup anteprima prenotazione -- */}
          <DialogWithActions
            fullScreen={false}
            fullWidth={true}
            open={previewDialogOpened}
            setOpen={setPreviewDialogOpened}
            title={"Anteprima Prenotazione"}
            actions={previewDialogActions}
          >
            <ReservationPreview value={selectedReservation as Reservation} />
          </DialogWithActions>
        </>
      </Container>
    </LoadWrapper>
  );
}

export default withRouter(ReservationsPage);
